// useEmbedMode.js — Detects iframe embed mode and provides helpers for embeddable payment pages.
// Usage: const { isEmbed, accentColor, postToParent, containerSx, paperSx } = useEmbedMode();
//
// Tenant embed example:
//   <iframe src="https://app.klonit.ai/pay/TOKEN?embed=true&accent=3b82f6"
//           style="width:100%;border:none;min-height:600px" id="klonit-pay"></iframe>
//   <script>
//     window.addEventListener('message', e => {
//       if (e.data?.type === 'klonit-resize') {
//         document.getElementById('klonit-pay').style.height = e.data.height + 'px';
//       }
//       if (e.data?.type === 'klonit-redirect') {
//         window.location.href = e.data.url;
//       }
//     });
//   </script>

import { useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const useEmbedMode = () => {
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  const accentColor = searchParams.get('accent') // hex without #
    ? `#${searchParams.get('accent').replace('#', '')}`
    : null;
  const observerRef = useRef(null);

  // Post messages to parent window
  const postToParent = useCallback((type, data = {}) => {
    if (!isEmbed) return;
    try {
      window.parent.postMessage({ type: `klonit-${type}`, ...data }, '*');
    } catch (e) { /* cross-origin silent fail */ }
  }, [isEmbed]);

  // Auto-resize: observe body height and notify parent
  useEffect(() => {
    if (!isEmbed) return;

    // Make html/body transparent
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';

    const sendHeight = () => {
      const height = document.documentElement.scrollHeight;
      postToParent('resize', { height });
    };

    // Initial + debounced resize observer
    sendHeight();
    observerRef.current = new ResizeObserver(() => sendHeight());
    observerRef.current.observe(document.body);

    return () => {
      observerRef.current?.disconnect();
      document.documentElement.style.background = '';
      document.body.style.background = '';
    };
  }, [isEmbed, postToParent]);

  // Shared sx overrides for embed mode
  const containerSx = isEmbed
    ? { mt: 0, mb: 0, px: { xs: 1, sm: 2 }, maxWidth: '100% !important' }
    : {};

  const paperSx = isEmbed
    ? {
        background: 'transparent',
        boxShadow: 'none',
        border: 'none',
        my: 0,
      }
    : {};

  const cardSx = isEmbed
    ? {
        boxShadow: 'none',
        border: '1px solid rgba(0,0,0,0.08)',
      }
    : {};

  // For redirects in embed mode: tell parent to navigate instead of self
  const embedRedirect = useCallback((url) => {
    if (isEmbed) {
      postToParent('redirect', { url });
    } else {
      window.location.href = url;
    }
  }, [isEmbed, postToParent]);

  // Preserve embed params when navigating to success/cancel pages
  const buildEmbedUrl = useCallback((basePath) => {
    if (!isEmbed) return basePath;
    const params = new URLSearchParams();
    params.set('embed', 'true');
    if (accentColor) params.set('accent', accentColor.replace('#', ''));
    const separator = basePath.includes('?') ? '&' : '?';
    return `${basePath}${separator}${params.toString()}`;
  }, [isEmbed, accentColor]);

  return {
    isEmbed,
    accentColor,
    postToParent,
    containerSx,
    paperSx,
    cardSx,
    embedRedirect,
    buildEmbedUrl,
  };
};

export default useEmbedMode;
